import React from 'react';
import { Button, Grid, MenuItem, TextField, Tooltip } from '@mui/material';
import { Mic, MicOff } from '@mui/icons-material';
import { VoiceBotOptions, ProjectInterface } from '../types';
import { realtimeVoices, voices, voicesStyles, AUDIO_CONFIG } from '../constants';

interface VoiceControlsProps {
    options: VoiceBotOptions;
    setOptions: (options: Partial<VoiceBotOptions>) => void;
    isStarted: boolean;
    isLoading: boolean;
    selectedProject: ProjectInterface;
    onStartStop: () => void;
}

export const VoiceControls: React.FC<VoiceControlsProps> = ({
    options,
    setOptions,
    isStarted,
    isLoading,
    selectedProject,
    onStartStop,
}) => {
    const speedOptions = Array.from(Array(AUDIO_CONFIG.SPEED_COUNT).keys()).map(
        (v) => `${Math.round((AUDIO_CONFIG.SPEED_MIN + AUDIO_CONFIG.SPEED_STEP * v) * 100) / 100}`
    );

    return (
        <Grid container spacing={2}>
            <Grid item>
                <TextField
                    select
                    size="small"
                    label="Voice"
                    disabled={isStarted}
                    value={options.voice}
                    onChange={({ target }) => {
                        setOptions({
                            voice: target.value,
                            style: '',
                        });
                    }}
                    sx={{ minWidth: '100px' }}
                >
                    {selectedProject.isRealtime
                        ? Object.keys(realtimeVoices).map((voice) => (
                              <MenuItem key={voice} value={voice}>
                                  {realtimeVoices[voice]}
                              </MenuItem>
                          ))
                        : Object.keys(voices).map((voice) => (
                              <MenuItem key={voice} value={voice}>
                                  {voices[voice]}
                              </MenuItem>
                          ))}
                </TextField>
            </Grid>
            <Grid item>
                <TextField
                    select
                    size="small"
                    label="Speed"
                    disabled={isStarted}
                    value={options.speed.toString()}
                    onChange={({ target }) => {
                        setOptions({
                            speed: parseFloat(target.value),
                        });
                    }}
                    sx={{ minWidth: '70px' }}
                >
                    {speedOptions.map((voiceSpeed) => (
                        <MenuItem key={`v${voiceSpeed}`} value={voiceSpeed}>
                            {voiceSpeed}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item>
                <TextField
                    select
                    size="small"
                    label="Style"
                    value={options.style}
                    onChange={({ target }) => {
                        setOptions({
                            style: target.value,
                        });
                    }}
                    disabled={isStarted || !voicesStyles[options.voice]}
                    sx={{ minWidth: '100px' }}
                >
                    {(voicesStyles[options.voice] ?? ['']).map((style) => (
                        <MenuItem key={style} value={style}>
                            {style}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item>
                <Tooltip title="Start a new audio conversation">
                    <Button
                        variant="contained"
                        onClick={onStartStop}
                        startIcon={isStarted ? <MicOff /> : <Mic />}
                        disabled={isLoading}
                    >
                        {isStarted ? 'Stop' : 'Start New'}
                    </Button>
                </Tooltip>
            </Grid>
        </Grid>
    );
};
